import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  deleteSavedUserOffer,
  getSavedUserOffer,
} from '../api/instant-lease-api';
import { isAuthenticated } from '../redux/auth/authUtils';
import {
  createLeaseApp,
  fetchLeaseApps,
} from '../redux/leaseApp/lease-app-slice';
import store, { RootState, useAppDispatch } from '../redux/store';
import { Offer, Partner } from '../types/instant-lease-api';

interface SavedUserOffer {
  data: {
    message: string;
    timestamp: string;
    status: string;
    user_details: Offer[];
  };
}

const useOfferCreation = (): Error | null => {
  // Auth State
  const auth = useSelector((state: RootState) => state.auth);
  // Dispatch hook
  const dispatch = useAppDispatch();
  const [leaseApplicationError, setLeaseApplicationError] =
    useState<Error | null>(null);

  useEffect(() => {
    // Get active lease application state from store
    const activeApplication = store.getState().leaseApp?.activeApplication;

    // Fetch lease applications
    const getLeaseApplications = () => {
      return new Promise<string>((resolve, reject) => {
        return dispatch(fetchLeaseApps({ accessToken: auth.accessToken }))
          .then(() => resolve('Getting Leaseing Application Fulfilled'))
          .catch((error) => reject(error));
      });
    };

    // Create new lease application
    const createNewLeasingApplication = (offer: Offer) => {
      return new Promise<string>((resolve, reject) => {
        const defaultPartner = offer?.partner ? offer?.partner : Partner.ALD;
        if (offer.offerId == null && offer.offerRefId == null) {
          reject('No valid offerId or offerRefId found on the offer');
          return;
        }
        // The offerhub URL can send offerId/offerRefId as the literal strings
        // "null" or (maybe) "undefined" when no real value exists. Both are truthy in
        // JS so we must explicitly filter them out.
        // Either offerId or offerRefId will have a real value — never both.
        const effectiveOfferId =
          offer?.offerId &&
          offer.offerId !== 'null' &&
          offer.offerId !== 'undefined'
            ? offer.offerId
            : undefined;
        const effectiveOfferRefId =
          offer?.offerRefId &&
          offer.offerRefId !== 'null' &&
          offer.offerRefId !== 'undefined'
            ? offer.offerRefId
            : undefined;

        const canCreate =
          (effectiveOfferId || effectiveOfferRefId) && defaultPartner;
        if (canCreate) {
          return dispatch(
            createLeaseApp({
              accessToken: auth.accessToken,
              partner: defaultPartner,
              offerId: effectiveOfferId,
              pricingId: offer?.pricingId,
              publicationId: offer?.publicationId,
              type: offer?.type,
              vin: offer?.vin,
              loyaltyId: offer?.loyaltyId,
              loyaltyType: offer?.loyaltyType,
              marketingData: offer?.marketingData,
              offerRefId: effectiveOfferRefId,
            }),
          )
            .then(unwrapResult)
            .then(() => {
              dispatch(fetchLeaseApps({ accessToken: auth.accessToken }));

              deleteSavedUserOffer({
                accessToken: auth.accessToken,
                email: auth?.email,
                offerId: effectiveOfferId ?? effectiveOfferRefId ?? null,
              }).then(() => {
                resolve('Lease application created and saved offer deleted');
              });
            })
            .catch((error) => {
              deleteSavedUserOffer({
                accessToken: auth.accessToken,
                email: auth?.email,
                offerId: effectiveOfferId ?? effectiveOfferRefId ?? null,
              }).then(() => {
                reject(`error creating leasing application ${error}`);
              });
            });
        }

        return false;
      });
    };

    // If user Authenticated
    if (isAuthenticated(auth) && auth.email) {
      setTimeout(() => {
        getSavedUserOffer({
          accessToken: auth.accessToken,
          email: auth?.email,
        })
          .then(({ data }: SavedUserOffer) => {
            if (data?.user_details.length > 0 && auth.emailVerified === true) {
              createNewLeasingApplication(
                data?.user_details[data?.user_details.length - 1],
              ).catch((error) => {
                setLeaseApplicationError(error);
              });
            }
          })
          .catch((error) => {
            setLeaseApplicationError(error);
          });
      }, 2000); // 2 seconds delay

      if (!activeApplication) {
        getLeaseApplications().catch((error) =>
          setLeaseApplicationError(error),
        );
      }
    }
  }, [auth, dispatch]);
  return leaseApplicationError;
};

export default useOfferCreation;
